// src/features/auth/ForgotPassword.jsx
//
// Step 1 of the OTP-gated password reset flow.
// Accepts email → POST /auth/forgot-password → navigates to /verify-reset-otp.
//
// Security: we always show the same "check your email" UI regardless of
// whether the backend found a matching account (prevents user-enumeration).

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button }   from '@/shared/components/ui/button';
import { Input }    from '@/shared/components/ui/input';
import { Label }    from '@/shared/components/ui/label';
import { authAPI }  from '@/api/auth.api';

// ─── Validation ───────────────────────────────────────────────────────────────

const validateEmail = (v) => {
  if (!v.trim())                                  return 'Email address is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return 'Please enter a valid email address.';
  return '';
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email,        setEmail]        = useState('');
  const [error,        setError]        = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateEmail(email);
    if (validationError) { setError(validationError); return; }

    setIsSubmitting(true);
    try {
      // Backend always returns 200 — no user-enumeration possible.
      await authAPI.forgotPassword(email.trim().toLowerCase());

      // Always navigate to the OTP step, regardless of whether the email
      // exists.  If it doesn't exist, the user will simply not receive a
      // code and will see the "no code?" help text.
      navigate('/verify-reset-otp', {
        state: { email: email.trim().toLowerCase() },
        replace: false,
      });
    } catch (err) {
      // Even on genuine server errors, surface a generic message.
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        // Treat everything else as "sent" to avoid leaking information.
        navigate('/verify-reset-otp', {
          state: { email: email.trim().toLowerCase() },
          replace: false,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left panel (image) ─────────────────────────────────────────────── */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=1200&q=80"
          alt="Gawher Ali Jewellery"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
          <div>
            <h2 className="font-playfair text-4xl mb-4">Gawher Ali Jewellery</h2>
            <p className="text-lg opacity-90">Handcrafted jewellery — premium quality at honest prices</p>
          </div>
        </div>
      </div>

      {/* ── Right panel (form) ─────────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key="forgot-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="w-full max-w-md"
          >
            {/* Back link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-antique-gold mb-8 transition-colors"
            >
              <ArrowLeft size={15} />
              Back to Sign In
            </Link>

            {/* Heading */}
            <div className="mb-8">
              <div className="w-12 h-12 rounded-full bg-antique-gold/10 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-antique-gold" />
              </div>
              <h1 className="font-playfair text-3xl mb-2">Forgot your password?</h1>
              <p className="text-gray-500 text-sm leading-relaxed">
                Enter the email linked to your account. We'll send you a
                6-digit verification code to reset your password.
              </p>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              >
                {error}
              </motion.div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  disabled={isSubmitting}
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="w-full btn-gold group"
              >
                {isSubmitting ? (
                  <span className="animate-pulse">Sending code…</span>
                ) : (
                  <>
                    Send Reset Code
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>

            {/* Footer */}
            <p className="mt-8 text-center text-sm text-gray-500">
              Remembered it?{' '}
              <Link to="/login" className="text-antique-gold hover:underline font-medium">
                Sign In
              </Link>
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}